import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { secureGet, secureDelete } from "../../config/api";
import { Helmet } from "react-helmet-async";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "../../components/PrimaryButton";
import DeletePopup from "../../components/DeletePopup";
import Filter from "../../components/Filter";
import OptionsMenu from "../../components/OptionsMenu";
import ModuleWrapper from "../../components/ModuleWrapper";
import LoadingLogo from "../../components/LoadingLogo";
import SnackbarNotification from "../../components/SnackbarNotification";
import PageScroller from "../../components/PageScroller";
import SidebarZoneEvents from "./SidebarZoneEvents";
import Card from "./Card";

import AddIcon from "../../icons/plusB.svg";
import EditIcon from "../../icons/edit.svg";
import DeleteIcon from "../../icons/trashR.svg";
import EyeIcon from "../../icons/eye.svg";

const Zones = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const topRef = useRef(null);
  const [open, setOpen] = useState(false);

  const [zones, setZones] = useState([]);

  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [inventories, setInventories] = useState([]);

  const [filters, setFilters] = useState({ needle: "", inventory: "", status: "" });

  const [pageNumber, setPageNumber] = useState(0); 
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);

  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showModule, setShowModule] = useState(false);
  const [selectedZone, setSelectedZone] = useState(null);

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const showSnackbar = (message, severity = "success", manual = false) =>
    setSnackbar({ open: true, message, severity, manual });

  const statusOptions = useMemo(
    () => [
      { label: t("inventory_status.CREATED"), value: "CREATED" },
      { label: t("inventory_status.NOT_STARTED"), value: "NOT_STARTED" },
      { label: t("inventory_status.IN_PROGRESS"), value: "IN_PROGRESS" },
      { label: t("inventory_status.SUBMITED"), value: "SUBMITED" },
      { label: t("inventory_status.TO_CONTROL"), value: "TO_CONTROL" },
      { label: t("inventory_status.CONTROL_NOT_STARTED"), value: "CONTROL_NOT_STARTED" },
      { label: t("inventory_status.CONTROL_IN_PROGRESS"), value: "CONTROL_IN_PROGRESS" },
      { label: t("inventory_status.CONTROLLED"), value: "CONTROLLED" },
      { label: t("inventory_status.IN_DISPUTE"), value: "IN_DISPUTE" },
      { label: t("inventory_status.VALIDATED"), value: "VALIDATED" }
    ],
    [t]
  );

  const filterOptions = [
    { name: "needle", type: "search" },
    {
      name: "inventory",
      label: t("fields.inventory.inventaire"),
      type: "dropdown",
      options: inventories.map((inv) => ({ label: inv.name, value: String(inv.id) })),
    },
    { name: "status", label: t("filters.orders.status"), type: "dropdown", options: statusOptions },
  ];

  useEffect(() => {
    (async () => {
      try {
        const res = await secureGet("/inventories?size=1000", showSnackbar, { signal });
        setInventories(res.data.content || []);
      } catch (error) {
        if (error.name !== "AbortError") console.error("Error fetching inventories:", error);
      }
    })();

    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPaginatedZones = useCallback(
    async (page = pageNumber, size = pageSize, active = filters) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("size", String(size));

        const n = (active.needle || "").trim();
        if (n) params.set("needle", n);

        const inv = (active.inventory || "").trim();
        if (inv) params.set("inventoryId", inv);

        const st = (active.status || "").trim();
        if (st) params.set("status", st);

        const response = await secureGet(`/inventory-zones?${params.toString()}`, showSnackbar, { signal });
        const data = response?.data ?? {};
        console.log(data);

        const content = data.content || [];
        setZones(content);
        setTotalPages(data.totalPages || 0);

        if (Number.isFinite(data.number)) setPageNumber(data.number);
        if (Number.isFinite(data.size)) setPageSize(data.size);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching zones:", error);
          setZones([]);
          setTotalPages(0);
        }
      } finally {
        setLoading(false);
      }
    },
    [filters, pageNumber, pageSize, signal]
  );

  useEffect(() => {
    fetchPaginatedZones(0, pageSize, { needle: "", inventory: "", status: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (name, value) => {
    const updated = { ...filters, [name]: value };
    setFilters(updated);
    setPageNumber(0);
    fetchPaginatedZones(0, pageSize, updated);
  };

  const resetFilters = () => {
    const reset = { needle: "", inventory: "", status: "" };
    setFilters(reset);
    setPageNumber(0);
    fetchPaginatedZones(0, pageSize, reset);
  };

  const handlePageChange = (newPage) => {
    setPageNumber(newPage);
    fetchPaginatedZones(newPage, pageSize, filters);
    topRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handlePageSizeChange = (newSize) => {
    const sizeNum = Number(newSize) || pageSize;
    setPageSize(sizeNum);
    setPageNumber(0);
    fetchPaginatedZones(0, sizeNum, filters);
  };

  const handleAddClick = () => {
    setSelectedZone(null);
    setShowModule(true);
  };

  const handleEditClick = (zone) => {
    setSelectedZone(zone);
    setShowModule(true);
  };

  const handleDeleteClick = (zone) => {
    setItemToDelete(zone);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      await secureDelete(`/inventory-zones/${itemToDelete.id}`, showSnackbar);
      await fetchPaginatedZones(pageNumber, pageSize, filters);
      showSnackbar(t("confirmation.inventory-zonesDelete"), "success");
    } catch (error) {
      console.error("Error deleting zone:", error);
    } finally {
      setDeletePopupVisible(false);
    }
  };

  const handleModuleClose = (action = "cancel") => {
    if (action === "submit") fetchPaginatedZones(pageNumber, pageSize, filters);
    setShowModule(false);
    setSelectedZone(null);
  };

  const handleDetails = (item) => {
    history.push({ pathname: `/zones/details/${item.id}`, state: { zoneName: item.name, zoneId: item.id } });
  };

  const getMenuOptions = (zone) => [
    { label: t("actions.details"), icon: EyeIcon, action: () => {
        setSelectedZone(zone);
        setOpen(true);
      } },
    { label: t("actions.modify"), icon: EditIcon, action: () => handleEditClick(zone) },
    { label: t("actions.delete"), icon: DeleteIcon, color: "#EF476F", action: () => handleDeleteClick(zone) },
  ];

  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox - {t("titles.inventory_zones")}</title>
      </Helmet>

      {/* Header */}
      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t("titles.inventory_zones")}</h1>
        <div className="ml-auto text-[16px] flex gap-3">
          <PrimaryButton label={t("primaryButtons.add_zone")} icon={AddIcon} onClick={handleAddClick} />
        </div>
      </div>

      {/* Filters */}
      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      {/* Content + Loading overlay */}
      <div style={{ position: "relative", minHeight: "200px" }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(255,255,255,0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1,
            }}
          >
            <LoadingLogo />
          </div>
        )}

        <div className="mt-6 grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {!loading && zones.length > 0 ? (
            zones.map((z) => (
              <Card
                key={z.id}
                label={z.inventoryName}
                title={`${z.name} - ${z.zoneManagerName}`}
                status={z.status}
                module="zone"
                optionsMenu={
                  <div onClick={(e) => e.stopPropagation()}>
                    <OptionsMenu options={getMenuOptions(z)} />
                  </div>
                }
              />
            ))
          ) : (
            !loading && (
              <div className="text-center mt-4 col-span-full">
              </div>
            )
          )}
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-6">
        <PageScroller
          pageNumber={pageNumber}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>

      {/* Delete */}
      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {/* Module */}
      {showModule && (
        <ModuleWrapper
          moduleType="inventory-zones"
          mode={selectedZone ? "modify" : "add"}
          onClose={handleModuleClose}
          onSuccess={(msg) => showSnackbar(msg, "success")}
          initialValues={selectedZone || {}}
        />
      )}
      {open && (
        <SidebarZoneEvents
          open={open}
          onClose={() => setOpen(false)}
          zoneId={selectedZone?.id} 
          zoneName={selectedZone?.name}
          zoneManagerName={selectedZone?.zoneManagerName}
          onDetails={() => {
            history.push({
              pathname: `/zones/details/${selectedZone.id}`,
              state: { zoneName: selectedZone.name }
            });
            setOpen(false);
          }}
        />
      )}

      {/* Snackbar */}
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Zones;
